/**
 * The bound a TEMPLATE's standing deposit and standing discount can be judged
 * by, and whether this edit is judged at all.
 *
 * A template carries defaults for an estimate that does not exist yet, so it
 * has no subtotal and no total to measure anything against. That splits the
 * two bases apart:
 *
 * - A PERCENT is bounded absolutely. Above 100% is meaningless whatever the
 *   future estimate comes to, so it is refused here — and refusing it here is
 *   the only chance anyone gets, because the operator who finds out otherwise
 *   is whoever tries to USE the template.
 * - A FLAT amount is not bounded here at all. There is no subtotal to compare
 *   it with, and a bound measured against a stand-in base would refuse
 *   standing discounts that are perfectly valid. The estimate built from the
 *   template is where a flat figure meets a real subtotal — that is what the
 *   estimate's own gate (`submit-gate.ts`) and the server's
 *   `assertDiscountWithinSubtotal` are for.
 *
 * The same split the server draws by passing a `null` total to
 * `assertDepositWithinTotal` / `assertDiscountWithinSubtotal`.
 *
 * ONE answer per figure, returned as the message to show: the inline error in
 * the defaults form and the save gate ask the same question, and a form that
 * says nothing while the gate blocks (or the reverse) is worse than either.
 */

export type StandingBasis = 'PERCENT' | 'FLAT';

/** A standing figure as the defaults form holds it: a basis and a typed string. */
export interface StandingFigure {
  type: StandingBasis;
  /** Raw form text — blank while nothing has been typed. */
  value: string;
}

/**
 * The defaults this module reads, structurally — NOT the form's whole value
 * type, which lives with the component and would import in a circle.
 */
export interface StandingDefaults {
  depositRequired: boolean | null;
  depositType: StandingBasis;
  depositValue: string;
  discountType: StandingBasis;
  discountValue: string;
}

export const STANDING_DEPOSIT_PERCENT_MESSAGE = 'Deposit can’t exceed 100%.';
export const STANDING_DISCOUNT_PERCENT_MESSAGE = 'Discount can’t exceed 100%.';

/** The amount a standing figure states, with a blank or unparseable one as none. */
export function standingAmount(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Is this standing figure a percent above 100?
 *
 * Strict `>`, so a whole-total 100% figure is allowed — the same threshold the
 * estimate and the server use.
 */
export function exceedsStandingPercent(figure: StandingFigure): boolean {
  return figure.type === 'PERCENT' && standingAmount(figure.value) > 100;
}

/** Clamp a percent figure as it is typed. A flat amount is never clamped. */
export function clampStandingPercentInput(type: StandingBasis, next: string): string {
  if (type !== 'PERCENT') return next;
  const parsed = Number.parseFloat(next);
  return Number.isFinite(parsed) && parsed > 100 ? '100' : next;
}

/**
 * Has the author TOUCHED this figure?
 *
 * The bound is a rule about INPUT. A template saved before the bound existed
 * may already store an oversized percent, and renaming it must not be blocked
 * by a figure nobody is editing — the server judges it only when the write
 * SETS it, and the form must not disagree. With nothing stored (a brand new
 * template) everything here is being set.
 */
export function standingFigureChanged(
  current: StandingFigure,
  stored: StandingFigure | undefined,
): boolean {
  if (!stored) return true;
  const currentAmount = standingAmount(current.value);
  if (currentAmount !== standingAmount(stored.value)) return true;
  // With no amount there is no figure to have a basis.
  return currentAmount > 0 && current.type !== stored.type;
}

/**
 * Why this standing deposit cannot be saved, or null.
 *
 * Switching a deposit ON is a deposit being SET even though the figure did not
 * move: a legacy `{ depositRequired: false, depositValue: 500 }` hydrates as
 * 500%, and flipping the switch makes that 500% effective for the first time.
 * The server counts that as a change (`patchesDeposit`); left out here, the
 * author got a generic 400 instead of the field-level error this exists for.
 */
export function standingDepositError(
  current: StandingDefaults,
  stored: StandingDefaults | undefined,
): string | null {
  // A deposit nobody switched on is not charged.
  if (current.depositRequired !== true) return null;
  const figure = { type: current.depositType, value: current.depositValue };
  if (!exceedsStandingPercent(figure)) return null;
  const turnedOn = stored?.depositRequired !== true;
  const beingSet =
    turnedOn ||
    standingFigureChanged(
      figure,
      stored && { type: stored.depositType, value: stored.depositValue },
    );
  return beingSet ? STANDING_DEPOSIT_PERCENT_MESSAGE : null;
}

/**
 * Why this standing discount cannot be saved, or null.
 *
 * No "switched on" case to mirror the deposit's: a discount has no flag, the
 * amount IS the switch (`templateDefaultsToDto` stores no basis without one),
 * so a discount becoming effective is always a value moving off zero — which
 * `standingFigureChanged` already reads as a change.
 */
export function standingDiscountError(
  current: StandingDefaults,
  stored: StandingDefaults | undefined,
): string | null {
  const figure = { type: current.discountType, value: current.discountValue };
  if (!exceedsStandingPercent(figure)) return null;
  const beingSet = standingFigureChanged(
    figure,
    stored && { type: stored.discountType, value: stored.discountValue },
  );
  return beingSet ? STANDING_DISCOUNT_PERCENT_MESSAGE : null;
}
